// src/pages/PublicSite.js
import { useEffect, useState } from "react";
import { getAbout, getPrograms, getTeam, getBoard, getStories, getJobs, getAnnouncements, getVideos } from "../firebase/firestore";

// ── Scroll Reveal ─────────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); });
    }, { threshold: 0.1, rootMargin: "0px 0px -40px 0px" });
    document.querySelectorAll(".reveal,.reveal-left,.reveal-right,.reveal-scale").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// ── Scroll Progress Back To Top ───────────────────────────
function BackToTop() {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const SIZE = 48, STROKE = 3, RADIUS = (SIZE - STROKE * 2) / 2;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
      setVisible(scrollTop > 300);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const strokeDashoffset = CIRCUMFERENCE - (progress / 100) * CIRCUMFERENCE;
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} title="Back to top"
      style={{ position:"fixed", bottom:"2rem", right:"2rem", zIndex:500, width:`${SIZE}px`, height:`${SIZE}px`, background:"var(--dark)", border:"none", borderRadius:"50%", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", opacity:visible?1:0, transform:visible?"translateY(0) scale(1)":"translateY(20px) scale(0.8)", transition:"all 0.4s cubic-bezier(0.34,1.56,0.64,1)", pointerEvents:visible?"all":"none", boxShadow:"0 4px 20px rgba(74,14,107,0.4)", padding:0 }}>
      <svg width={SIZE} height={SIZE} style={{ position:"absolute", top:0, left:0, transform:"rotate(-90deg)" }}>
        <circle cx={SIZE/2} cy={SIZE/2} r={RADIUS} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={STROKE} />
        <circle cx={SIZE/2} cy={SIZE/2} r={RADIUS} fill="none" stroke="var(--gold)" strokeWidth={STROKE} strokeLinecap="round" strokeDasharray={CIRCUMFERENCE} strokeDashoffset={strokeDashoffset} style={{ transition:"stroke-dashoffset 0.2s ease" }} />
      </svg>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ position:"relative", zIndex:1 }}>
        <polyline points="18,15 12,9 6,15"/>
      </svg>
    </button>
  );
}

// ── Video Modal ───────────────────────────────────────────
function VideoModal({ videoId, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="video-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="video-modal-inner">
        <button className="video-close" onClick={onClose}>✕</button>
        <iframe src={`https://www.youtube.com/embed/${videoId}?autoplay=1`} title="Video" allow="autoplay; encrypted-media" allowFullScreen />
      </div>
    </div>
  );
}

function getYTId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&\n?#]+)/);
  return match ? match[1] : null;
}

// ── WhatsApp Button ───────────────────────────────────────
function WhatsAppButton() {
  const phone = "256704152220";
  const message = encodeURIComponent("Hello MotherChild Empowerment Initiative, I'd like to get in touch.");
  return (
    <a
      href={`https://wa.me/${phone}?text=${message}`}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-btn"
      title="Chat on WhatsApp"
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.117 1.526 5.845L0 24l6.335-1.509A11.934 11.934 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.366l-.36-.213-3.76.895.952-3.67-.234-.376A9.818 9.818 0 1112 21.818z"/>
      </svg>
      <div className="whatsapp-tooltip">Chat with us on WhatsApp</div>
    </a>
  );
}

// ── Navbar ────────────────────────────────────────────────
function Navbar({ hasAnn }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [["Home","#home"],["About","#about"],["Programs","#programs"],["Stories","#stories"],["Gallery","#gallery"],["Team","#team"],["FAQ","#faq"],["Contact","#contact"]];
  return (
    <>
      <nav className={`navbar ${scrolled ? "scrolled" : ""} ${hasAnn ? "navbar-with-ann" : ""}`}>
        <div className="nav-inner">
          <a className="nav-logo" href="#home"><img src="/logo-dark.png" alt="Mother Child Empowerment Initiative home" /></a>
          <ul className="nav-links">
            {links.map(([l,h]) => <li key={l}><a href={h}>{l}</a></li>)}
          </ul>
          <a className="nav-donate" href="#donate">Donate</a>
          <button className={`hamburger ${menuOpen ? "open" : ""}`} aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>
            <span/><span/><span/>
          </button>
        </div>
      </nav>
      <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
        {links.map(([l,h]) => <a key={l} href={h} onClick={() => setMenuOpen(false)}>{l}</a>)}
        <a href="#donate" className="mobile-cta-btn" onClick={() => setMenuOpen(false)}>Donate</a>
      </div>
    </>
  );
}

// ── Hero — copy on plum, photos in an arch, labelled slide tabs ──
function HeroSection() {
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [videoModal, setVideoModal] = useState(null);
  const videoId1 = "esLmYuogO3s";
  const DURATION = 7000;

  const slides = [
    {
      tab:"Who we are",
      headline:"Every Child Deserves a Chance to Thrive.", hl:"Chance to Thrive",
      sub:"We work with vulnerable girls and young mothers in Kiyindi, Sembabule and Busunju to build rewarding futures.",
      img:"/hero-slide1.jpg", pos:"center 30%", caption:"Children in our community programs", video:true,
      btn1:{ label:"See our programs", href:"#programs" },
      btn2:{ label:"Watch our story", action:"video" }
    },
    {
      tab:"EmpowerHer",
      headline:"Skills That Turn Young Mothers into Breadwinners.", hl:"Breadwinners",
      sub:"Our flagship program trains teenage mothers and young women in tailoring, money skills and business.",
      img:"/photos/prog-empowerher-1.jpg", pos:"60% center", caption:"EmpowerHer trainees in the tailoring workshop",
      btn1:{ label:"Explore EmpowerHer", href:"#programs" },
      btn2:{ label:"Sponsor a trainee", href:"#donate" }
    },
    {
      tab:"EduChild",
      headline:"Keeping Girls in the Classroom.", hl:"Classroom",
      sub:"Scholarships, uniforms and school supplies for vulnerable children aged 3–17, with girls first in line.",
      img:"/photos/prog-educhild.jpg", pos:"center 40%", caption:"Pupils supported through EduChild",
      btn1:{ label:"Sponsor a child", href:"#donate" },
      btn2:{ label:"Read success stories", href:"#stories" }
    },
    {
      tab:"Get involved",
      headline:"Your Support Changes Lives for Good.", hl:"Changes Lives",
      sub:"Every contribution helps a girl or young mother build a more independent future.",
      img:"/hero-slide3.jpg", pos:"center 30%", caption:"Girls and mothers we work with",
      btn1:{ label:"Donate", href:"#donate" },
      btn2:{ label:"Watch our story", action:"video" }
    },
  ];
  const n = slides.length;
  const go = i => setSlide((i + n) % n);

  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (paused || reduce) return;
    const t = setTimeout(() => setSlide(s => (s + 1) % n), DURATION);
    return () => clearTimeout(t);
  }, [slide, paused, n]);

  const s = slides[slide];
  return (
    <section className={`hero3 ${paused ? "is-paused" : ""}`} id="home" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} aria-roledescription="carousel">
      <div className="hero3-bg" aria-hidden="true">
        {slides.map((x, i) => (
          <img key={x.img} src={x.img} alt="" className={i === slide ? "on" : ""} style={{ objectPosition:x.pos }} />
        ))}
      </div>
      <div className="hero3-veil" aria-hidden="true" />
      <div className="hero3-inner">
        <div className="hero3-top">
        <div className="hero2-text" key={slide}>
          <h1>{(() => { const [pre, post] = s.headline.split(s.hl); return <>{pre}<span className="hl">{s.hl}</span>{post}</>; })()}</h1>
          <p className="hero2-sub">{s.sub}</p>
          <div className="hero2-btns">
            <a className="btn-marigold" href={s.btn1.href}>{s.btn1.label}</a>
            {s.btn2.action === "video"
              ? <button className="btn-line" onClick={() => setVideoModal(videoId1)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="6,3 20,12 6,21"/></svg>
                  {s.btn2.label}
                </button>
              : <a className="btn-line" href={s.btn2.href}>{s.btn2.label}</a>
            }
          </div>
        </div>
          {s.video && (
            <button className="hero3-video" key={`v${slide}`} onClick={() => setVideoModal(videoId1)} aria-label="Play a message from our founder and director">
              <img src={`https://img.youtube.com/vi/${videoId1}/maxresdefault.jpg`} alt="" onError={e => { e.currentTarget.src = `https://img.youtube.com/vi/${videoId1}/hqdefault.jpg`; }} />
              <span className="hero3-video-play" aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><polygon points="7,4 20,12 7,20"/></svg>
              </span>
              <span className="hero3-video-label"><small>Watch a message from</small>Our Founder &amp; Director</span>
            </button>
          )}
        </div>
        <div className="hero3-bottom">
          <div className="hero2-tabs" role="tablist" aria-label="Choose a slide">
            {slides.map((x, i) => (
              <button key={i} role="tab" aria-selected={i === slide} className={i === slide ? "on" : ""} onClick={() => go(i)}>
                <span className="hero2-tab-bar"><span key={i === slide ? `p${slide}` : "idle"} style={{ animationDuration:`${DURATION}ms` }} /></span>
                <span className="hero2-tab-label">{x.tab}</span>
              </button>
            ))}
          </div>
          <div className="hero2-arrows">
            <button onClick={() => go(slide - 1)} aria-label="Previous slide">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <span className="hero2-count">{slide + 1} / {n}</span>
            <button onClick={() => go(slide + 1)} aria-label="Next slide">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        </div>
      </div>
      <p className="sr-only" aria-live="polite">{s.caption}</p>
      {videoModal && <VideoModal videoId={videoModal} onClose={() => setVideoModal(null)} />}
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────
function StatsBar() {
  return (
    <div className="stats-bar">
      {[{num:"200+",label:"Children served"},{num:"60%",label:"Improved attendance"},{num:"3+",label:"Communities"},{num:"5",label:"Programs"}].map((s,i) => (
        <div className="stat-item reveal" key={i} style={{transitionDelay:`${i*0.1}s`}}>
          <div className="stat-num-bar">{s.num}</div>
          <div className="stat-lbl">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Programs ──────────────────────────────────────────────
const PROGRAMS = [
  { key:"educhild", name:"EduChild", sub:"Girl child education", img:"/photos/prog-educhild.jpg", pos:"center 40%",
    desc:"Scholarships, scholastic materials, uniforms and school-retention support for vulnerable children aged 3–17 years, prioritising girls, so that no child drops out for lack of fees." },
  { key:"herflow", name:"HerFlow", sub:"Menstrual hygiene day", img:"/photos/prog-herflow.jpg", pos:"center 30%",
    desc:"Menstrual health days in schools and communities that give girls the knowledge and support to manage their periods with dignity and stay in class every day of the month." },
  { key:"srh", name:"SRH Education", sub:"Sexual and reproductive health", img:"/photos/prog-srh.jpg", pos:"center 35%",
    desc:"Age-appropriate sexual and reproductive health sessions in schools and communities, equipping girls and young women to make informed, confident choices about their bodies and futures." },
  { key:"outreach", name:"Community Outreach", sub:"Reaching families where they are", img:"/photos/prog-outreach.jpg", pos:"center 35%",
    desc:"Visits, awareness sessions and support for vulnerable households in the communities we serve, building trust and connecting families to our education, health and skilling programs." },
];

function ProgramsSection() {
  const [shot, setShot] = useState(0);
  const flagshipShots = ["/photos/prog-empowerher-1.jpg", "/photos/prog-empowerher-2.jpg", "/photos/prog-empowerher-3.jpg"];
  return (
    <section className="section programs-section" id="programs">
      <div className="container">
        <div className="sec-head">
          <h2 className="section-title">Our Programs</h2>
          <p>Five programs, one goal: girls and women who are educated, healthy, skilled and able to stand on their own.</p>
        </div>

        {/* Flagship — EmpowerHer */}
        <div className="flagship reveal">
          <div className="flagship-media">
            <img src={flagshipShots[shot]} alt="EmpowerHer participants at tailoring training" />
                        <div className="flagship-thumbs">
              {flagshipShots.map((s, i) => (
                <button key={s} className={i === shot ? "on" : ""} onClick={() => setShot(i)} aria-label={`Show photo ${i + 1}`}>
                  <img src={s} alt="" />
                </button>
              ))}
            </div>
          </div>
          <div className="flagship-body">
            <div className="flagship-kicker">Our flagship program</div>
            <h3>EmpowerHer</h3>
            <p className="flagship-lead">Hands-on vocational skilling for teenage mothers and young women, so they can earn a living, care for their children and build independent futures.</p>
            <ul className="flagship-list">
              <li><strong>Tailoring & garment making</strong> on industrial machines, from first stitch to finished product</li>
              <li><strong>Financial literacy & savings</strong> to manage income and plan for the future</li>
              <li><strong>Entrepreneurship support</strong> to start and grow small businesses</li>
              <li><strong>Mentorship & peer support</strong> from women who have walked the same road</li>
            </ul>
            <div className="flagship-btns">
              <a className="btn-gold" href="#donate">Sponsor a trainee</a>
              <a className="flagship-link" href="#stories">Read Agnes's story</a>
            </div>
          </div>
        </div>

        {/* Other programs */}
        <div className="prog-cards">
          {PROGRAMS.map((p, i) => (
            <article key={p.key} className="prog-card reveal" style={{ transitionDelay:`${i*0.08}s` }}>
              <div className="prog-card-img"><img src={p.img} alt={`${p.name} program`} style={{ objectPosition:p.pos }} loading="lazy" /></div>
              <div className="prog-card-body">
                <div className="prog-card-sub">{p.sub}</div>
                <h4>{p.name}</h4>
                <p>{p.desc}</p>
                <a href="#donate" className="prog-card-link">Support this program</a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Gallery ───────────────────────────────────────────────
const GALLERY = [
  { src:"/photos/prog-empowerher-2.jpg", cap:"EmpowerHer trainees in the tailoring workshop", cls:"g-wide" },
  { src:"/photos/gallery-banner.jpg", cap:"Our vision, mission and values", cls:"g-tall" },
  { src:"/photos/prog-empowerher-1.jpg", cap:"Young mothers learning a trade with their babies close by", cls:"" },
  { src:"/photos/gallery-team.jpg", cap:"The MotherChild team", cls:"" },
  { src:"/photos/prog-educhild.jpg", cap:"Pupils supported through our programs", cls:"g-wide" },
  { src:"/photos/gallery-girls.jpg", cap:"Girls receiving support packs", cls:"g-tall" },
  { src:"/photos/gallery-team-bags.jpg", cap:"Our team preparing support packs for girls", cls:"" },
  { src:"/photos/prog-srh.jpg", cap:"A school session with pupils", cls:"" },
  { src:"/photos/prog-empowerher-3.jpg", cap:"Practice makes progress at the sewing machines", cls:"" },
  { src:"/photos/prog-outreach.jpg", cap:"Out in the community", cls:"" },
  { src:"/photos/gallery-team-guests.jpg", cap:"Our team with guests and partners", cls:"" },
  { src:"/photos/prog-herflow.jpg", cap:"Every girl deserves dignity", cls:"" },
];

function GallerySection() {
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (open === null) return;
    const onKey = e => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen(o => (o + 1) % GALLERY.length);
      if (e.key === "ArrowLeft") setOpen(o => (o - 1 + GALLERY.length) % GALLERY.length);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open]);
  return (
    <section className="section gallery-section" id="gallery">
      <div className="container">
        <div className="sec-head">
          <h2 className="section-title">Moments From Our Work</h2>
          <p style={{ color:"var(--gray)", fontSize:"0.95rem", maxWidth:"520px", margin:"0 auto" }}>From the tailoring workshop to the classroom, meet the girls, mothers and team behind every story.</p>
        </div>
        <div className="gal-grid">
          {GALLERY.map((g, i) => (
            <figure key={g.src} className={`gal-item ${g.cls} reveal`} onClick={() => setOpen(i)}>
              <img src={g.src} alt={g.cap} loading="lazy" />
              <figcaption>{g.cap}</figcaption>
            </figure>
          ))}
        </div>
      </div>
      {open !== null && (
        <div className="gal-lightbox" onClick={() => setOpen(null)}>
          <button className="gal-btn gal-close" aria-label="Close" onClick={() => setOpen(null)}>×</button>
          <button className="gal-btn gal-prev" aria-label="Previous" onClick={e => { e.stopPropagation(); setOpen((open - 1 + GALLERY.length) % GALLERY.length); }}>←</button>
          <figure onClick={e => e.stopPropagation()}>
            <img src={GALLERY[open].src} alt={GALLERY[open].cap} />
            <figcaption>{GALLERY[open].cap}</figcaption>
          </figure>
          <button className="gal-btn gal-next" aria-label="Next" onClick={e => { e.stopPropagation(); setOpen((open + 1) % GALLERY.length); }}>→</button>
        </div>
      )}
    </section>
  );
}

// ── About — real community photo + fixed title spacing ────
function AboutSection({ about }) {
  return (
    <section className="section" id="about" style={{ background:"var(--purple-pale)" }}>
      <div className="container">
        <div className="about-grid">
          <div className="reveal-left about-image-col">
            <div style={{ position:"relative", borderRadius:"20px", overflow:"hidden", aspectRatio:"4/5" }}>
              <img
                src={about?.imageUrl || "/about-photo.jpg"}
                alt="Children in our community programs"
                style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center", }}
              />
              <div style={{ position:"absolute", inset:0, background:"linear-gradient(135deg,rgba(74,14,107,0.12),transparent,rgba(245,197,24,0.06))", pointerEvents:"none" }} />
              <div style={{ position:"absolute", bottom:"1rem", left:"1rem", right:"1rem", background:"rgba(26,10,38,0.75)", backdropFilter:"blur(8px)", borderRadius:"12px", padding:"0.75rem 1rem", display:"flex", alignItems:"center", gap:"0.6rem" }}>
                <div style={{ width:"8px", height:"8px", borderRadius:"50%", background:"var(--gold)", flexShrink:0 }}></div>
                <span style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.85)", fontWeight:500 }}>Empowering girls & mothers across Uganda since 2023</span>
              </div>
            </div>
          </div>
          <div className="reveal-right about-text-col">
            {/* Fixed title spacing — tighter line height */}
            <h2 style={{ fontFamily:"var(--font-display)", fontSize:"clamp(1.8rem,3.5vw,2.6rem)", lineHeight:1.15, marginBottom:"1rem", color:"var(--dark)" }}>
A Women-Led Nonprofit for Girls and Young Mothers
            </h2>
            <p style={{ color:"var(--gray)", lineHeight:1.85, marginBottom:"1rem", fontSize:"0.95rem" }}>
              {about?.description || "Mother Child Empowerment Initiative is a women-led nonprofit dedicated to improving education and wellbeing of young girls and women. We focus on skills building, children scholarships, sexual and reproductive health education and economic empowerment for young mothers."}
            </p>
            <p style={{ color:"var(--gray)", lineHeight:1.85, marginBottom:"1.75rem", fontSize:"0.95rem" }}>
              {about?.description2 || "We work with vulnerable children prioritizing girls and young mothers from marginalized communities of Uganda to create rewarding futures. Founded in 2023, serving over 200 children across Kiyindi, Sembabule, and Busunju."}
            </p>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem", marginBottom:"1.75rem" }}>
              <div style={{ background:"white", borderRadius:"16px", padding:"1.25rem", borderLeft:"4px solid var(--purple)" }}>
                <div style={{ fontSize:"0.68rem", fontWeight:700, color:"var(--purple)", letterSpacing:"0.1em", marginBottom:"0.4rem" }}>Our Vision</div>
                <p style={{ fontSize:"0.82rem", color:"var(--gray)", lineHeight:1.65, margin:0 }}>A future where every girl and woman rises, educated, healthy, confident and unstoppable.</p>
              </div>
              <div style={{ background:"white", borderRadius:"16px", padding:"1.25rem", borderLeft:"4px solid var(--gold)" }}>
                <div style={{ fontSize:"0.68rem", fontWeight:700, color:"var(--gold-dark)", letterSpacing:"0.1em", marginBottom:"0.4rem" }}>Our Mission</div>
                <p style={{ fontSize:"0.82rem", color:"var(--gray)", lineHeight:1.65, margin:0 }}>To advance education and wellbeing of young girls through quality learning, scholarships, skills and SRH education.</p>
              </div>
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:"0.6rem" }}>
              {["Compassion","Integrity","Excellence","Community"].map((v,i) => (
                <span key={i} style={{ background:"var(--purple-light)", color:"var(--purple)", fontSize:"0.78rem", fontWeight:600, padding:"5px 14px", borderRadius:"50px" }}>{v}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Founder — redesigned with pattern background ──────────
function FounderSection() {
  return (
    <section className="section founder-section" id="founder">
      {/* Decorative SVG background pattern */}
      <div className="founder-pattern">
        <svg width="100%" height="100%" viewBox="0 0 800 500" preserveAspectRatio="xMidYMid slice" style={{ position:"absolute", inset:0, opacity:0.06 }}>
          <defs>
            <pattern id="dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="2" fill="#F5C518"/>
            </pattern>
          </defs>
          <rect width="800" height="500" fill="url(#dots)"/>
          <circle cx="100" cy="100" r="80" stroke="#4A0E6B" strokeWidth="1" fill="none" opacity="0.5"/>
          <circle cx="700" cy="400" r="120" stroke="#F5C518" strokeWidth="1" fill="none" opacity="0.4"/>
          <circle cx="400" cy="50" r="60" stroke="#4A0E6B" strokeWidth="1" fill="none" opacity="0.3"/>
        </svg>
      </div>

      <div className="container" style={{ position:"relative", zIndex:2 }}>
        <div className="sec-head">
          <h2 className="section-title">Born From Purpose and Lived Experience</h2>
          <p className="section-sub" style={{ margin:"0 auto" }}>The story behind MotherChild Empowerment Initiative.</p>
        </div>

        <div className="founder-card-wrapper reveal">
          {/* Left — photo with decorative frame */}
          <div className="founder-photo-side">
            <div className="founder-photo-frame">
              <div className="founder-photo-deco-ring" />
              <div className="founder-photo-inner">
                <img src="/founder.png" alt="Nakimbugwe Joan" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} />
              </div>
              {/* Gold accent badge */}
              <div className="founder-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--gold)"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></svg>
                <span>Founder, 2023</span>
              </div>
            </div>
            <div style={{ textAlign:"center", marginTop:"1.5rem" }}>
              <div style={{ fontFamily:"var(--font-display)", fontSize:"1.2rem", fontWeight:700, color:"var(--dark)" }}>Nakimbugwe Joan</div>
              <div style={{ fontSize:"0.8rem", color:"var(--purple)", fontWeight:600, background:"var(--purple-light)", display:"inline-block", padding:"4px 16px", borderRadius:"50px", marginTop:"0.35rem" }}>Founder &amp; Executive Director</div>
            </div>
          </div>

          {/* Right — story with pull quotes */}
          <div className="founder-story-side">
            <div className="founder-pull-quote">
              <div style={{ fontFamily:"var(--font-display)", fontSize:"1.25rem", color:"var(--purple)", fontStyle:"italic", lineHeight:1.6 }}>
                "Every child deserves a chance to thrive, yet millions are denied this basic right. That's why MotherChild Empowerment Initiative exists."
              </div>
            </div>
            <p style={{ color:"var(--gray)", lineHeight:1.9, marginBottom:"1rem", fontSize:"0.95rem" }}>
              At age 8, Joan's life changed in a way no child should have to endure. Her mother, struggling to make ends meet, could not afford school fees for all her 8 children. Joan dropped out of school and was sent to live with her grandfather, while her siblings continued their education.
            </p>
            <p style={{ color:"var(--gray)", lineHeight:1.9, marginBottom:"1rem", fontSize:"0.95rem" }}>
              Despite these challenges, Joan refused to let her circumstances define her future. With the encouragement of her grandfather, a retired primary teacher, she found hope again. After a year, she enrolled back into primary school in Masaka, earned a half bursary for secondary school, and combined academics with working on the school farm for six years.
            </p>
            <p style={{ color:"var(--gray)", lineHeight:1.9, marginBottom:"1.5rem", fontSize:"0.95rem" }}>
              Her perseverance paid off when she received a government sponsorship to attend Makerere University. In 2023, carrying the memory of her struggles, she founded MotherChild Empowerment Initiative to ensure that no girl feels forgotten and no mother is left without support.
            </p>
            {/* Timeline milestones */}
            <div className="founder-timeline">
              {[{year:"Age 8",event:"Dropped out of school"},{year:"Age 9",event:"Re-enrolled with grandfather's help"},{year:"S1–S6",event:"Earned bursary, worked on school farm"},{year:"2023",event:"Founded MotherChild Initiative"}].map((m,i) => (
                <div key={i} className="founder-milestone">
                  <div className="milestone-dot" />
                  <div className="milestone-year">{m.year}</div>
                  <div className="milestone-event">{m.event}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Video Section ─────────────────────────────────────────
function VideoSection() {
  const [videoModal, setVideoModal] = useState(null);
  const videos = [
    { id:"esLmYuogO3s", title:"Our Work in the Community", desc:"See how MotherChild is making a real difference across Uganda" },
    { id:"RIIIYipZyik", title:"A Beneficiary's Story", desc:"Hear directly from one of the young women we have supported" },
    { id:"6TTM8C7GURM", title:"A Testimony", desc:"A powerful testimony from someone whose life was transformed" },
  ];
  return (
    <section className="video-section" id="impact">
      <div className="container">
        <div className="sec-head">
          <h2 className="section-title" style={{ color:"white" }}>See Our Work in Action</h2>
          <p style={{ color:"rgba(255,255,255,0.5)", fontSize:"0.95rem", maxWidth:"500px", margin:"0 auto" }}>Watch how we are transforming lives of girls and mothers across Uganda.</p>
        </div>
        <div className="video-grid-3">
          {videos.map((v, i) => (
            <div key={i} className="video-card reveal" style={{ transitionDelay:`${i*0.15}s` }} onClick={() => setVideoModal(v.id)}>
              <div className="video-thumb" style={{ position:"relative", overflow:"hidden" }}>
                <img src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`} alt={v.title} style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e => { e.target.style.display="none"; }} />
                <div className="video-play-overlay">
                  <div className="video-play-btn">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#3D2A00"><polygon points="5,3 19,12 5,21"/></svg>
                  </div>
                </div>
              </div>
              <div className="video-label">
                <h4>{v.title}</h4>
                <p>{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {videoModal && <VideoModal videoId={videoModal} onClose={() => setVideoModal(null)} />}
    </section>
  );
}

// ── Success Story ─────────────────────────────────────────
function StoriesSection({ stories }) {
  const [videoModal, setVideoModal] = useState(null);
  const defaultStory = {
    id:"1", highlight:"From setback to self-reliance",
    quote:"Agnes couldn't continue school after Senior Four due to lack of fees. She became pregnant and moved to Kampala to work as a housemaid, leaving her baby in the care of her grandmother. MotherChild Empowerment Initiative recognised her determination and supported her to enroll in a catering course. In 2025, Agnes graduated with a certificate in catering, equipped with the skills to earn an income, support her child, and build a more hopeful future. Her journey is a powerful reminder that with the right support, even the most difficult situations can be transformed into new beginnings.",
    name:"Agnes", location:"Kampala, Uganda", badge:"Catering graduate, 2025", photoUrl:"/agnes.png"
  };
  const story = (stories && stories.length) ? stories[0] : defaultStory;
  return (
    <section className="section" id="stories" style={{ background:"var(--purple-pale)" }}>
      <div className="container">
        <div className="sec-head">
          <h2 className="section-title">Success Stories</h2>
          <p className="section-sub" style={{ margin:"0 auto" }}>With the right support, even the most difficult situations become powerful new beginnings.</p>
        </div>
        <div className="story-full reveal">
          <div className="story-full-img">
            <img src={story.photoUrl||"/agnes.png"} alt={story.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} />
            <div className="story-full-gradient" />
          </div>
          <div className="story-full-content">
            {story.highlight && <div style={{ fontSize:"0.7rem", fontWeight:700, color:"var(--gold)", letterSpacing:"0.1em", marginBottom:"0.75rem" }}>{story.highlight}</div>}
            <div style={{ fontFamily:"var(--font-display)", fontSize:"4rem", color:"rgba(255,255,255,0.12)", lineHeight:1, marginBottom:"-0.5rem" }}>"</div>
            <p style={{ color:"rgba(255,255,255,0.85)", fontSize:"1rem", lineHeight:1.9, marginBottom:"2rem" }}>{story.quote}</p>
            <div style={{ display:"flex", alignItems:"center", gap:"1rem", borderTop:"1px solid rgba(255,255,255,0.12)", paddingTop:"1.25rem", flexWrap:"wrap" }}>
              <div style={{ width:"52px", height:"52px", borderRadius:"50%", overflow:"hidden", border:"3px solid var(--gold)", flexShrink:0 }}>
                <img src={story.photoUrl||"/agnes.png"} alt={story.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} />
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:"1rem", color:"white" }}>{story.name}</div>
                <div style={{ fontSize:"0.8rem", color:"rgba(255,255,255,0.5)" }}>{story.location}</div>
              </div>
              {story.badge && <div style={{ marginLeft:"auto", background:"rgba(245,197,24,0.15)", color:"var(--gold)", fontSize:"0.72rem", fontWeight:700, padding:"4px 12px", borderRadius:"50px", border:"1px solid rgba(245,197,24,0.3)" }}>{story.badge}</div>}
            </div>
          </div>
        </div>
        {stories && stories.length > 1 && (
          <div className="cards-grid" style={{ marginTop:"1.5rem" }}>
            {stories.slice(1).map((s, i) => (
              <div className="story-card reveal" key={s.id} style={{ transitionDelay:`${(i+1)*0.1}s` }}>
                <div className="story-img-wrap">{s.photoUrl ? <img className="story-img" src={s.photoUrl} alt={s.name} /> : <div className="story-img-placeholder"/>}</div>
                <div className="story-body">
                  <div className="story-quote">"</div>
                  <p className="story-text">{s.quote}</p>
                  <div className="story-foot">
                    <div className="story-av">{s.photoUrl ? <img src={s.photoUrl} alt={s.name}/> : "👩"}</div>
                    <div><div className="story-name">{s.name}</div><div className="story-loc">{s.location}</div></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {videoModal && <VideoModal videoId={videoModal} onClose={() => setVideoModal(null)} />}
    </section>
  );
}

// ── Team — completely redesigned ──────────────────────────
function TeamSection({ team, board }) {
  const defaultTeam = [
    { id:"1", name:"Nakimbugwe Joan", role:"Founder & Executive Director", isFounder:true, photoUrl:"/founder.png" },
    { id:"2", name:"Kisitu Steven", role:"Programs Director", photoUrl:"/team-kisitu.jpg" },
    { id:"3", name:"Nantale Joreen Rebecca", role:"Finance & Administration Officer", photoUrl:"/team-nantale.jpg" },
    { id:"4", name:"Mrs. Magumba Catherine", role:"Community Development Officer", photoUrl:"/team-magumba.jpg" },
  ];
  const defaultBoard = [
    { id:"1", name:"Ms. Gladys Soi", role:"Board Chair", isChair:true },
    { id:"2", name:"Ms. Dorothy Nalumansi", role:"Board Member", photoUrl:"/board-dorothy.jpg" },
    { id:"3", name:"Ms. Ruth Nampenja", role:"Board Member" },
  ];
  const teamList = (team && team.length) ? team : defaultTeam;
  const boardList = (board && board.length) ? board : defaultBoard;

  return (
    <section className="section" id="team" style={{ background:"var(--night)", overflow:"hidden", position:"relative" }}>
      {/* Subtle background pattern */}
      <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(245,197,24,0.04) 1px, transparent 1px)", backgroundSize:"32px 32px", pointerEvents:"none" }} />

      <div className="container" style={{ position:"relative", zIndex:2 }}>
        {/* Team header */}
        <div className="sec-head">
          <h2 className="section-title" style={{ color:"white" }}>Meet the Team</h2>
          <p style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.95rem", maxWidth:"460px", margin:"0 auto" }}>Passionate changemakers committed to girls' and women's empowerment every day.</p>
        </div>

        {/* Team cards — new horizontal card design */}
        <div className="team-cards-new">
          {teamList.map((m, i) => (
            <div className="team-card-new reveal" key={m.id} style={{ transitionDelay:`${i*0.1}s` }}>
              <div className="team-card-photo">
                {m.photoUrl
                  ? <img src={m.photoUrl} alt={m.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} />
                  : <div style={{ width:"100%", height:"100%", background:"linear-gradient(160deg,#1A0A26,#2D0845,#4A0E6B)", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:"0.75rem" }}>
                      <div style={{ width:"64px", height:"64px", borderRadius:"50%", background:"rgba(245,197,24,0.12)", border:"2px dashed rgba(245,197,24,0.35)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(245,197,24,0.55)" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </div>
                      <span style={{ fontSize:"0.6rem", color:"rgba(245,197,24,0.45)", fontWeight:600, textAlign:"center", letterSpacing:"0.05em" }}>Photo coming<br/>soon</span>
                    </div>
                }
                {m.isFounder && <div className="team-card-badge">Founder</div>}
              </div>
              <div className="team-card-info">
                <h4 className="team-card-name">{m.name}</h4>
                <div className="team-card-role">{m.role}</div>
                {m.bio && <p className="team-card-bio">{m.bio}</p>}
              </div>
            </div>
          ))}
        </div>

        {/* Board section */}
        <div className="sec-head">
          <h2 className="section-title" style={{ color:"white", fontSize:"1.8rem" }}>Board of Directors</h2>
          <p style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.9rem", maxWidth:"440px", margin:"0 auto" }}>Providing strategic leadership and oversight to advance our mission.</p>
        </div>

        <div className="board-cards-new">
          {boardList.map((m, i) => (
            <div className="board-card-new reveal" key={m.id} style={{ transitionDelay:`${i*0.1}s` }}>
              <div className="board-card-photo">
                {m.photoUrl
                  ? <img src={m.photoUrl} alt={m.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} />
                  : <div style={{ width:"100%", height:"100%", background:"linear-gradient(160deg,#1A0A26 0%,#2D0845 50%,#4A0E6B 100%)", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:"0.75rem" }}>
                      <div style={{ width:"56px", height:"56px", borderRadius:"50%", background:"rgba(245,197,24,0.15)", border:"2px dashed rgba(245,197,24,0.4)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(245,197,24,0.6)" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </div>
                      <span style={{ fontSize:"0.62rem", color:"rgba(245,197,24,0.5)", fontWeight:600, textAlign:"center", letterSpacing:"0.05em" }}>Photo coming<br/>soon</span>
                    </div>
                }
              </div>
              <div className="board-card-info">
                <h4 className="board-card-name">{m.name}</h4>
                <div className="board-card-role">{m.role}</div>
                {m.isChair && <div className="board-chair-badge">Board Chair</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Jobs ──────────────────────────────────────────────────
function JobsSection({ jobs }) {
  const open = (jobs || []).filter(j => j.status !== "closed");
  return (
    <section className="section" id="jobs" style={{ background:"var(--purple)" }}>
      <div className="container">
        <div className="sec-head">
          <h2 className="section-title" style={{ color:"white" }}>Open Positions</h2>
        </div>
        {!open.length
          ? <p style={{ textAlign:"center", color:"rgba(255,255,255,0.4)" }}>No open positions right now. Check back soon.</p>
          : <div style={{ display:"flex", flexDirection:"column", gap:"1rem", maxWidth:"800px", margin:"0 auto" }}>
              {open.map((j,i) => (
                <div key={j.id} className="reveal" style={{ background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:"16px", padding:"1.25rem 1.75rem", display:"flex", alignItems:"center", justifyContent:"space-between", gap:"1rem", transitionDelay:`${i*0.1}s` }}>
                  <div>
                    <div style={{ fontFamily:"var(--font-display)", fontSize:"1rem", color:"white", marginBottom:"0.5rem" }}>{j.title}</div>
                    <div style={{ display:"flex", gap:"0.5rem", flexWrap:"wrap" }}>
                      <span style={{ background:"rgba(245,197,24,0.15)", color:"var(--gold)", fontSize:"0.68rem", fontWeight:600, padding:"2px 8px", borderRadius:"50px" }}>{j.department}</span>
                      <span style={{ background:"rgba(255,255,255,0.08)", color:"rgba(255,255,255,0.6)", fontSize:"0.68rem", fontWeight:600, padding:"2px 8px", borderRadius:"50px" }}>📍 {j.location}</span>
                      <span style={{ background:"rgba(255,255,255,0.08)", color:"rgba(255,255,255,0.6)", fontSize:"0.68rem", fontWeight:600, padding:"2px 8px", borderRadius:"50px" }}>{j.type}</span>
                    </div>
                  </div>
                  <a href="#contact" className="btn-gold" style={{ whiteSpace:"nowrap", fontSize:"0.8rem", padding:"0.5rem 1.2rem", display:"inline-flex", alignItems:"center", justifyContent:"center" }}>Apply</a>
                </div>
              ))}
            </div>
        }
      </div>
    </section>
  );
}

// ── FAQ Section ───────────────────────────────────────────
function FAQSection() {
  const [open, setOpen] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const FAQ_PREVIEW = 5;
  const faqs = [
    { q:"What is MotherChild Empowerment?", a:"MotherChild Empowerment is a community-based non-profit organization committed to empowering women and supporting vulnerable children through education, mentorship, skills development, and community empowerment initiatives." },
    { q:"Is MotherChild Empowerment legally registered?", a:"Yes. MotherChild Empowerment operates under the legally registered entity MotherChild Empowerment Initiative based in Uganda." },
    { q:"What is EmpowerHer Initiative?", a:"EmpowerHer Initiative is a program under MotherChild Empowerment Initiative focused on empowering women and supporting vulnerable girls through mentorship and skills training." },
    { q:"Who do you support?", a:"We support vulnerable girls, women and young mothers with skilling programs, children aged 3–17 years facing educational and social challenges with educational scholarships, and families in need of empowerment opportunities." },
    { q:"How can I support MotherChild Empowerment?", a:"You can support us by donating, volunteering, partnering with us, sponsoring a beneficiary, and sharing our work within your community." },
    { q:"Do you accept volunteers?", a:"Yes. We welcome volunteers who are passionate about community development, women empowerment, education, mentorship, and social impact." },
    { q:"How can organizations or businesses partner with you?", a:"Organizations and businesses can partner with us through sponsorships, training support, collaborative projects, donations, and community outreach initiatives." },
    { q:"How are donations used?", a:"Donations are used to support educational initiatives, skills training, mentorship activities, and empowerment programs for vulnerable women and children." },
    { q:"How do you select beneficiaries?", a:"Beneficiaries are identified based on vulnerability, need, and alignment with the objectives of our programs and available resources." },
    { q:"How can I stay updated on your activities?", a:"You can stay updated by following our social media platforms (@empowerher_ug on Instagram) and visiting our website regularly for news, stories, and program updates." },
    { q:"Are your programs faith-based?", a:"Our values are inspired by faith, compassion, hope, and service while remaining inclusive and supportive of all individuals regardless of background." },
  ];
  return (
    <section className="section" id="faq" style={{ background:"white" }}>
      <div className="container">
        <div className="sec-head">
          <h2 className="section-title">Questions People Ask Us</h2>
          <p className="section-sub" style={{ margin:"0 auto" }}>Everything you need to know about MotherChild Empowerment Initiative.</p>
        </div>
        <div style={{ maxWidth:"860px", display:"flex", flexDirection:"column", borderTop:"1px solid var(--purple-light)" }}>
          {(showAll ? faqs : faqs.slice(0, FAQ_PREVIEW)).map((f, i) => (
            <div key={i} className="reveal" style={{ transitionDelay:`${i*0.04}s`, background:"transparent", borderBottom:"1px solid var(--purple-light)", overflow:"hidden" }}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                style={{ width:"100%", padding:"1.35rem 0", display:"flex", alignItems:"center", justifyContent:"space-between", gap:"1rem", background:"none", border:"none", cursor:"pointer", textAlign:"left" }}
              >
                <span style={{ fontSize:"1.12rem", fontWeight:650, color: open === i ? "var(--purple)" : "var(--dark)", lineHeight:1.4 }}>{f.q}</span>
                <span style={{ width:"28px", height:"28px", borderRadius:"50%", background: open === i ? "var(--purple)" : "var(--purple-light)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all 0.3s ease" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={open === i ? "white" : "var(--purple)"} strokeWidth="2.5" strokeLinecap="round" style={{ transform: open === i ? "rotate(180deg)" : "rotate(0deg)", transition:"transform 0.3s ease" }}>
                    <polyline points="6,9 12,15 18,9"/>
                  </svg>
                </span>
              </button>
              {open === i && (
                <div style={{ padding:"0 3rem 1.5rem 0" }}>
                  <p style={{ color:"var(--gray)", fontSize:"1rem", lineHeight:1.7, maxWidth:"65ch" }}>{f.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        {faqs.length > FAQ_PREVIEW && (
          <button className="faq-more" onClick={() => { setShowAll(v => !v); if (showAll) setOpen(o => (o !== null && o >= FAQ_PREVIEW ? null : o)); }} aria-expanded={showAll}>
            {showAll ? "Show fewer questions" : `Show all ${faqs.length} questions`}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" style={{ transform: showAll ? "rotate(180deg)" : "none", transition:"transform .25s ease" }}><polyline points="6,9 12,15 18,9"/></svg>
          </button>
        )}
      </div>
    </section>
  );
}

// ── Donate Section ────────────────────────────────────────
function DonateSection() {
  return (
    <section className="section" id="donate" style={{ background:"var(--purple-pale)" }}>
      <div className="container">
        <div className="sec-head">
          <h2 className="section-title">Ways to Give</h2>
          <p className="section-sub" style={{ margin:"0 auto" }}>Your generosity directly empowers girls and mothers across Uganda. Every contribution makes a difference.</p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:"1.5rem" }}>
          {[
            { title:"Sponsor a Child", desc:"Cover school fees, uniforms and scholastic materials for a vulnerable child aged 3–17 years.", cta:"Sponsor a child" },
            { title:"Give to All Programs", desc:"Support our programs in education, skills training, mentorship and SRH education for women and girls.", cta:"Donate" },
            { title:"Partner With Us", desc:"Businesses and organizations can partner through sponsorships, collaborative projects and community outreach.", cta:"Talk to us" },
          ].map((d, i) => (
            <div key={i} className="reveal" style={{ transitionDelay:`${i*0.1}s`, background:"white", borderRadius:"16px", padding:"2rem 1.75rem", textAlign:"left", borderTop:"4px solid var(--gold)" }}>
              <h3 style={{ fontFamily:"var(--font-display)", fontSize:"1.4rem", fontWeight:700, color:"var(--dark)", marginBottom:"0.75rem" }}>{d.title}</h3>
              <p style={{ color:"var(--gray)", fontSize:"0.875rem", lineHeight:1.75, marginBottom:"1.5rem" }}>{d.desc}</p>
              <a href="#contact" style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", background:"var(--purple)", color:"white", padding:"0.65rem 1.5rem", borderRadius:"50px", fontSize:"0.82rem", fontWeight:600, textDecoration:"none", transition:"all 0.25s ease" }}
                onMouseEnter={e => { e.currentTarget.style.background="var(--purple-mid)"; e.currentTarget.style.transform="translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background="var(--purple)"; e.currentTarget.style.transform="translateY(0)"; }}
              >{d.cta}</a>
            </div>
          ))}
        </div>

        {/* Payment methods — bank coming soon */}
        <div style={{ margin:"2rem 0 0", background:"white", borderRadius:"16px", padding:"1.75rem 2rem", textAlign:"left" }} className="reveal">
          <h3 style={{ fontFamily:"var(--font-display)", fontSize:"1.25rem", fontWeight:700, color:"var(--dark)", marginBottom:"0.9rem" }}>How to Pay</h3>
          <div style={{ display:"flex", justifyContent:"flex-start", gap:"0.75rem", flexWrap:"wrap", marginBottom:"1rem" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"0.5rem", background:"var(--purple-pale)", padding:"0.5rem 1rem", borderRadius:"50px", fontSize:"0.82rem", fontWeight:600, color:"var(--purple)" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.63a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .84h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8a16 16 0 006.09 6.09l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              Mobile money
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:"0.5rem", background:"var(--gold-light)", padding:"0.5rem 1rem", borderRadius:"50px", fontSize:"0.82rem", fontWeight:600, color:"var(--gold-dark)" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              Bank transfer
              <span style={{ fontSize:"0.65rem", background:"var(--gold)", color:"#3D2A00", padding:"1px 6px", borderRadius:"50px", marginLeft:"2px" }}>Coming soon</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:"0.5rem", background:"#E8F5E9", padding:"0.5rem 1rem", borderRadius:"50px", fontSize:"0.82rem", fontWeight:600, color:"#2E7D32" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51z"/></svg>
              WhatsApp
            </div>
          </div>
          <p style={{ color:"var(--gray)", fontSize:"0.98rem", lineHeight:1.7, maxWidth:"70ch" }}>
            To donate, reach us directly via WhatsApp on <strong>+256 704 152 220</strong> or send us a message through our contact form below. Bank account details will be available soon.
          </p>
        </div>
      </div>
    </section>
  );
}

// ── Contact Section ───────────────────────────────────────
function ContactSection() {
  const [form, setForm] = useState({ name:"", email:"", subject:"", message:"" });
  const [sent, setSent] = useState(false);
  const handleSubmit = (e) => { e.preventDefault(); setSent(true); };
  return (
    <section className="section" id="contact" style={{ background:"white" }}>
      <div className="container">
        <div className="contact-grid">
          <div className="reveal-left">
            <h2 className="section-title">Get in Touch</h2>
            <p style={{ color:"var(--gray)", marginBottom:"2rem", lineHeight:1.8 }}>Whether you want to volunteer, donate, partner with us or learn more, we'd love to hear from you.</p>
            {[{label:"Phone",val:"+256 704 152 220",cls:"p"},{label:"Location",val:"Uganda (Kiyindi, Sembabule & Busunju)",cls:"g"},{label:"Organisation",val:"Mother Child Empowerment Initiative",cls:"p"}].map((item,i) => (
              <div className="contact-info-item" key={i}>
                <div>
                  <div style={{ fontSize:"0.7rem", fontWeight:700, color:"var(--purple)", letterSpacing:"0.05em" }}>{item.label}</div>
                  <div style={{ fontSize:"0.9rem", color:"var(--dark)", fontWeight:500 }}>{item.val}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="reveal-right">
            {sent
              ? <div style={{ textAlign:"center", padding:"3rem", background:"var(--purple-pale)", borderRadius:"20px", border:"1px solid var(--purple-light)" }}>
                  <img src="/logo-dark.png" alt="MotherChild" style={{ height:"60px", marginBottom:"1rem" }} />
                  <h3 style={{ marginBottom:"0.5rem" }}>Thank You</h3>
                  <p style={{ color:"var(--gray)" }}>We'll be in touch soon.</p>
                </div>
              : <div className="contact-form-wrap">
                  <div className="form-group"><label>Your name</label><input required placeholder="Your full name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></div>
                  <div className="form-group"><label>Email address</label><input required type="email" placeholder="your@email.com" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></div>
                  <div className="form-group"><label>Subject</label><input placeholder="How can we help?" value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} /></div>
                  <div className="form-group"><label>Message</label><textarea required placeholder="Tell us more..." value={form.message} onChange={e=>setForm({...form,message:e.target.value})} /></div>
                  <button className="btn-primary" onClick={handleSubmit}>Send message</button>
                </div>
            }
          </div>
        </div>
      </div>
    </section>
  );
}

// ── CTA ───────────────────────────────────────────────────
function CTASection() {
  return (
    <section className="cta-section">
      <div className="container">
        <div className="reveal">
          <h2>Be Part of the Change</h2>
          <p>Every girl and every mother deserves the opportunity to thrive. Join us in making that a reality.</p>
          <div className="cta-btns">
            <a className="btn-gold" href="#donate">Donate</a>
            <a className="btn-white-outline" href="#contact">Volunteer</a>
            <a className="btn-white-outline" href="#contact">Partner with us</a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="footer">
      <div className="footer-grid">
        <div>
          <div className="footer-logo"><img src="/logo-light.png" alt="Mother Child Empowerment Initiative" /></div>
          <p className="footer-desc">A women-led nonprofit dedicated to improving education and wellbeing of young girls and women across Uganda.</p>
          <div className="footer-social">
            {[
              { s:"f", href:"#" },
              { s:"ig", href:"https://instagram.com/empowerher_ug" },
              { s:"in", href:"#" },
              { s:"tw", href:"#" }
            ].map((item,i) => <a key={i} href={item.href} className="social-btn" target="_blank" rel="noopener noreferrer">{item.s}</a>)}
          </div>
          <ul className="footer-contact">
            <li><span>Call us</span><a href="tel:+256704152220">+256 704 152 220</a></li>
            <li><span>WhatsApp</span><a href="https://wa.me/256704152220" target="_blank" rel="noopener noreferrer">Chat with us</a></li>
            <li><span>Where we work</span>Kiyindi, Sembabule &amp; Busunju, Uganda</li>
            <li><span>Instagram</span><a href="https://instagram.com/empowerher_ug" target="_blank" rel="noopener noreferrer">@empowerher_ug</a></li>
          </ul>
        </div>
        <div>
          <h5>Explore</h5>
          <ul className="footer-two-col">
            {[["About","#about"],["Programs","#programs"],["Our impact","#impact"],["Our team","#team"],["Stories","#stories"],["Gallery","#gallery"],["Donate","#donate"],["FAQ","#faq"],["Jobs","#jobs"],["Contact","#contact"]].map(([l,h]) => (
              <li key={l}><a href={h}>{l}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h5>Get involved</h5>
          <ul>
            <li><a href="#contact">Volunteer</a></li>
            <li><a href="#contact">Donate</a></li>
            <li><a href="#contact">Partner with us</a></li>
            <li><a href="#jobs">Work with us</a></li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} Mother Child Empowerment Initiative. All rights reserved.</span>
        <span>Designed by <a href="https://www.linkedin.com/in/asiimwe-isaac-756a61a7" target="_blank" rel="noopener noreferrer">izackcreates</a></span>
      </div>
    </footer>
  );
}

// ── Main ──────────────────────────────────────────────────
export default function PublicSite() {
  const [data, setData] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  useScrollReveal();

  useEffect(() => {
    async function load() {
      try {
        const [about, programs, team, board, stories, jobs, anns, videos] = await Promise.all([
          getAbout(), getPrograms(), getTeam(), getBoard(), getStories(), getJobs(), getAnnouncements(), getVideos()
        ]);
        setData({ about, programs, team, board, stories, jobs, videos });
        setAnnouncements((anns || []).filter(a => a.active));
      } catch (err) { console.error(err); }
    }
    load();
  }, []);

  const activeAnn = announcements[0];

  return (
    <div>
      <BackToTop />
      <WhatsAppButton />
      {activeAnn && <div className="ann-bar">{activeAnn.text} <span>{activeAnn.cta || "Learn more"}</span></div>}
      <Navbar hasAnn={!!activeAnn} />
      <HeroSection />
      <StatsBar />
      <ProgramsSection />
      <AboutSection about={data.about} />
      <FounderSection />
      <VideoSection />
      <StoriesSection stories={data.stories} />
      <GallerySection />
      <TeamSection team={data.team} board={data.board} />
      <JobsSection jobs={data.jobs} />
      <DonateSection />
      <FAQSection />
      <ContactSection />
      <CTASection />
      <Footer />
    </div>
  );
}
